// CreditBalance: Display user's current credit balance with upgrade prompts
// Integrates with existing UI for seamless workflow enhancement

import React from 'react'
import { useCredits } from '@/hooks/useCredits'
import { useSubscription } from '@/hooks/useSubscription'
import { CreditBalanceSkeleton } from './SkeletonLoader'

interface CreditBalanceProps {
  className?: string
  showUpgradePrompt?: boolean
  minimal?: boolean
  onClick?: () => void
}

export function CreditBalance({ 
  className = '', 
  showUpgradePrompt = true,
  minimal = false,
  onClick 
}: CreditBalanceProps) {
  const { balance, isLoading, error, hasCredits, isTrialUser } = useCredits()
  const { isSubscribed, createCheckoutSession } = useSubscription()

  if (isLoading) {
    return minimal ? (
      <div className={`animate-pulse ${className}`}>
        <div className="h-6 bg-gray-200 rounded w-20"></div>
      </div>
    ) : (
      <CreditBalanceSkeleton />
    )
  }

  if (error) {
    return (
      <div className={`text-red-600 text-sm ${className}`}>
        Credit status unavailable
      </div>
    )
  }

  if (!balance) {
    return null
  }

  // Determine display color based on credit level
  const getBalanceColor = () => {
    if (balance.balance === 0) return 'text-red-600'
    if (balance.balance <= 10) return 'text-yellow-600'
    return 'text-green-600'
  }

  // Determine warning message
  const getWarningMessage = () => {
    if (balance.balance === 0) {
      return isTrialUser ? 'Trial credits exhausted' : 'No credits remaining'
    }
    if (balance.balance <= 10) {
      return 'Credits running low'
    }
    return null
  }

  const handleUpgradeClick = async () => {
    if (onClick) {
      onClick()
      return
    }

    // Default upgrade behavior - redirect to pricing
    window.location.href = '/#pricing'
  }

  if (minimal) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <span className={`font-medium ${getBalanceColor()}`}>
          {balance.balance} credits
        </span>
        {isTrialUser && (
          <span className="text-xs text-gray-500">
            ({balance.trial_credits_remaining} trial)
          </span>
        )}
      </div>
    )
  }

  return (
    <div className={`bg-white rounded-lg border p-4 ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-900">Credit Balance</h3>
        {!isSubscribed && showUpgradePrompt && (
          <button
            onClick={handleUpgradeClick}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            Upgrade
          </button>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Available</span>
          <span className={`font-semibold ${getBalanceColor()}`}>
            {balance.balance}
          </span>
        </div>

        {isTrialUser && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Trial remaining</span>
            <span className="font-medium text-gray-900">
              {balance.trial_credits_remaining}
            </span>
          </div>
        )}

        {isSubscribed && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Used this month</span>
            <span className="font-medium text-gray-700">
              {balance.used_this_month}
            </span>
          </div>
        )}

        {getWarningMessage() && (
          <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-xs text-yellow-800">
              {getWarningMessage()}
              {!isSubscribed && (
                <>
                  {' '}
                  <button
                    onClick={handleUpgradeClick}
                    className="font-medium underline hover:no-underline"
                  >
                    Upgrade now
                  </button>
                </>
              )}
            </p>
          </div>
        )}

        {!hasCredits && !isSubscribed && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
            <p className="text-sm text-red-800 font-medium mb-2">
              No credits remaining
            </p>
            <p className="text-xs text-red-700 mb-3">
              Upgrade to a paid plan to continue processing images with AI.
            </p>
            <button
              onClick={handleUpgradeClick}
              className="w-full bg-red-600 text-white py-2 px-3 rounded text-sm font-medium hover:bg-red-700 transition-colors"
            >
              View Plans
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default CreditBalance